import {
  type ComponentProps,
  type ComponentType,
  type ReactElement,
} from 'react';

import {
  Flex,
  Icon,
  ListBox,
  ListBoxRow,
  MenuDivider,
  MenuSearch,
  MenuSearchField,
  Text,
  listRowClassNames,
} from '@spotify-internal/encore-web';
import { Box } from '@spotify-internal/encore-web/next';

import styles from './EnumFilterMenu.module.css';

export type EnumFilterMenuOption<T extends string = string> = {
  value: T;
  label: string;
  icon?: ComponentType<{ className?: string }>;
};

type EnumFilterMenuProps<T extends string = string> = {
  label: string;
  options: EnumFilterMenuOption<T>[];
  selectedValues: T[];
  onValueChange: (values: T[]) => void;
  onClose: () => void;
  search?: { placeholder: string };
};

export const EnumFilterMenu = <T extends string>({
  label,
  options,
  selectedValues,
  onValueChange,
  onClose,
  search,
}: EnumFilterMenuProps<T>): ReactElement => {
  const selectableValues = options.map(option => option.value);
  const handleSelectionChange: NonNullable<
    ComponentProps<typeof ListBox>['onSelectionChange']
  > = keys => {
    onValueChange(
      keys === 'all'
        ? selectableValues
        : [
            ...selectedValues.filter(
              value => !selectableValues.includes(value),
            ),
            ...selectableValues.filter(value => keys.has(value)),
          ],
    );
  };

  const content = (
    <>
      {options.length > 0 && (
        <>
          <button
            type="button"
            className={listRowClassNames({
              size: 'small',
              variant: 'naked',
              isInteractive: true,
              className: styles.bulkAction,
            })}
            onClick={() =>
              onValueChange(selectedValues.length > 0 ? [] : selectableValues)
            }
            data-track-id={
              selectedValues.length > 0 ? 'menu.clear-all' : 'menu.select-all'
            }
            data-track-action="update"
           data-track-source-anchor="b644507b24;enum-filter-menu-button">
            {selectedValues.length > 0 ? 'Clear all' : 'Select all'}
          </button>
          <MenuDivider />
        </>
      )}
      <ListBox
        aria-label={label}
        autoFocus={!search}
        selectionMode="multiple"
        selectedKeys={selectedValues}
        onSelectionChange={handleSelectionChange}
        rowSize="small"
        emptyState={
          <Text as="span" variant="bodySmall" semanticColor="textSubdued">
            No results
          </Text>
        }
      >
        {options.map(option => (
          <ListBoxRow
            key={option.value}
            id={option.value}
            textValue={option.label}
            data-track-id="menu.filter-option"
            data-track-action="update"
          >
            {option.icon ? (
              <Flex alignItems="center" gap="tighter-2">
                <Icon
                  as={option.icon}
                  size="small"
                  semanticColor="textSubdued"
                  aria-hidden
                />
                <Text as="span" variant="bodySmall">
                  {option.label}
                </Text>
              </Flex>
            ) : (
              option.label
            )}
          </ListBoxRow>
        ))}
      </ListBox>
    </>
  );

  return (
    <Box
      onKeyDownCapture={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
      data-track-scope="list.filter-options"
      data-track-data-filter-label={label}
    >
      {search ? (
        <MenuSearch>
          <MenuSearchField
            aria-label={label}
            placeholder={search.placeholder}
          />
          {content}
        </MenuSearch>
      ) : (
        content
      )}
    </Box>
  );
};
